import { useEffect } from 'react';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { Skeleton } from '@/components/ui/skeleton';

interface FeatureSettingsGuardProps {
  readonly children: React.ReactNode;
  readonly fallback?: React.ReactNode;
}

/**
 * FeatureSettingsGuard ensures that feature settings are fully initialized
 * before rendering children. This prevents race conditions where screens
 * check the demo/live mode before feature settings have been loaded.
 */
export function FeatureSettingsGuard({ children, fallback }: FeatureSettingsGuardProps): React.ReactElement {
  const { isInitialized } = useFeatureSettings();

  useEffect(() => {
    if (isInitialized) {
      console.log('[FeatureSettingsGuard] Feature settings initialized');
    }
  }, [isInitialized]);

  if (!isInitialized) {
    return (
      <>
        {fallback || (
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}
